import { lazy, Suspense } from 'react';
import { Outlet, Navigate, useRoutes } from 'react-router-dom';

import PrivateRoute from './PrivateRoute';
import DashboardLayout from '../layouts/dashboard';
import PublicLayout from '../layouts/public/PublicLayout';
import ErrorBoundary from '../components/ErrorBoundary';

// Public pages with lazy loading and error handling
const HomePage = lazy(() =>
  import('../pages/HomePage').catch(() => ({
    default: () => <div>Error loading HomePage</div>,
  }))
);
const AboutPage = lazy(() =>
  import('../pages/AboutPage').catch(() => ({
    default: () => <div>Error loading AboutPage</div>,
  }))
);
const PricingPage = lazy(() =>
  import('../pages/PricingPage').catch(() => ({
    default: () => <div>Error loading PricingPage</div>,
  }))
);
const ContactPage = lazy(() =>
  import('../pages/ContactPage').catch(() => ({
    default: () => <div>Error loading ContactPage</div>,
  }))
);
const TermsPage = lazy(() =>
  import('../pages/TermsPage').catch(() => ({
    default: () => <div>Error loading TermsPage</div>,
  }))
);
const PrivacyPage = lazy(() =>
  import('../pages/PrivacyPage').catch(() => ({
    default: () => <div>Error loading PrivacyPage</div>,
  }))
);
const SignupPage = lazy(() =>
  import('../pages/SignupPage').catch(() => ({
    default: () => <div>Error loading SignupPage</div>,
  }))
);
const LoginPage = lazy(() =>
  import('../pages/Login').catch(() => ({ default: () => <div>Error loading LoginPage</div> }))
);
const PaymentPage = lazy(() =>
  import('../pages/PaymentPage').catch(() => ({
    default: () => <div>Error loading PaymentPage</div>,
  }))
);
const SupportLogin = lazy(() =>
  import('../pages/SupportLogin').catch(() => ({
    default: () => <div>Error loading SupportLogin</div>,
  }))
);
const SupportDashboard = lazy(() =>
  import('../pages/SupportDashboard').catch(() => ({
    default: () => <div>Error loading SupportDashboard</div>,
  }))
);
const SupportAuthGuard = lazy(() =>
  import('../components/Support/SupportAuthGuard').catch(() => ({
    default: () => <div>Error loading SupportAuthGuard</div>,
  }))
);

// Private pages with lazy loading and error handling
const IndexPage = lazy(() =>
  import('../pages/app').catch(() => ({ default: () => <div>Error loading Dashboard</div> }))
);
const UserPage = lazy(() =>
  import('../pages/user').catch(() => ({ default: () => <div>Error loading User Page</div> }))
);
const PeoplePage = lazy(() =>
  import('../pages/PeoplePage').catch(() => ({
    default: () => <div>Error loading People Page</div>,
  }))
);
const AssetsPage = lazy(() =>
  import('../pages/AssetsPage').catch(() => ({
    default: () => <div>Error loading Assets Page</div>,
  }))
);
const Page404 = lazy(() =>
  import('../pages/page-not-found').catch(() => ({
    default: () => <div>Error loading 404 Page</div>,
  }))
);
const CompanyPage = lazy(() =>
  import('../pages/CompanyPage').catch(() => ({
    default: () => <div>Error loading Company Page</div>,
  }))
);
const LocationsPage = lazy(() =>
  import('../pages/LocationsPage').catch(() => ({
    default: () => <div>Error loading Locations Page</div>,
  }))
);
const DepartmentPage = lazy(() =>
  import('../pages/DepartmentPage').catch(() => ({
    default: () => <div>Error loading Department Page</div>,
  }))
);
const CategoryPage = lazy(() =>
  import('../pages/CategoryPage').catch(() => ({
    default: () => <div>Error loading Category Page</div>,
  }))
);
const AssetForm = lazy(() =>
  import('../components/CompanyAssets/AssetForm').catch(() => ({
    default: () => <div>Error loading Asset Form</div>,
  }))
);
const AssetDetailPage = lazy(() =>
  import('../components/CompanyAssets/AssetDetailsPage').catch(() => ({
    default: () => <div>Error loading Asset Details</div>,
  }))
);
const UserActivityTimeline = lazy(() =>
  import('../components/User/UserActivityTimeline').catch(() => ({
    default: () => <div>Error loading User Activity</div>,
  }))
);
const ReportingPage = lazy(() =>
  import('../pages/ReportingPage').catch(() => ({
    default: () => <div>Error loading Reporting Page</div>,
  }))
);
const BillingPage = lazy(() =>
  import('../pages/BillingPage').catch(() => ({
    default: () => <div>Error loading Billing Page</div>,
  }))
);

// ----------------------------------------------------------------------

export default function Router() {
  const routes = useRoutes([
    // Public routes
    {
      element: (
        <PublicLayout>
          <ErrorBoundary>
            <Suspense
              fallback={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '200px',
                    fontSize: '18px',
                    color: '#666',
                  }}
                >
                  Loading page...
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </PublicLayout>
      ),
      children: [
        { element: <HomePage />, index: true },
        { path: 'about', element: <AboutPage /> },
        { path: 'pricing', element: <PricingPage /> },
        { path: 'contact', element: <ContactPage /> },
        { path: 'terms', element: <TermsPage /> },
        { path: 'privacy', element: <PrivacyPage /> },
        { path: 'signup', element: <SignupPage /> },
        { path: 'login', element: <LoginPage /> },
        { path: 'payment', element: <PaymentPage /> },
        { path: 'support/login', element: <SupportLogin /> },
      ],
    },
    // Support routes (separate from main app routes)
    {
      path: 'support',
      element: (
        <ErrorBoundary>
          <Suspense
            fallback={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '200px',
                  fontSize: '18px',
                  color: '#666',
                }}
              >
                Loading support portal...
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      ),
      children: [
        {
          path: 'dashboard',
          element: (
            <SupportAuthGuard>
              <SupportDashboard />
            </SupportAuthGuard>
          ),
        },
      ],
    },
    // Private routes
    {
      element: (
        <PrivateRoute>
          <DashboardLayout>
            <ErrorBoundary>
              <Suspense
                fallback={
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '200px',
                      fontSize: '18px',
                      color: '#666',
                    }}
                  >
                    Loading dashboard...
                  </div>
                }
              >
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </DashboardLayout>
        </PrivateRoute>
      ),
      children: [
        { path: 'dashboard', element: <IndexPage /> },
        { path: 'user-activities/:userId', element: <UserActivityTimeline /> },
        { path: 'assets/view-asset/:id', element: <AssetDetailPage /> },
        { path: 'assets/:id/edit', element: <AssetForm /> },
        { path: 'departments', element: <DepartmentPage /> },
        { path: 'locations', element: <LocationsPage /> },
        { path: 'categories', element: <CategoryPage /> },
        { path: 'asset-list', element: <AssetsPage /> },
        { path: 'companies', element: <CompanyPage /> },
        { path: 'assets/new', element: <AssetForm /> },
        { path: 'people', element: <PeoplePage /> },
        { path: 'user', element: <UserPage /> },
        { path: 'billing', element: <BillingPage /> },
        { path: 'reporting', element: <ReportingPage /> },
        { path: '/', element: <Navigate to="/dashboard" replace /> },
      ],
    },
    // Error pages
    {
      path: '404',
      element: <Page404 />,
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return routes;
}
